import { CHUNK_SIZE } from "./constants";
import { getBlockIndex } from "./chunk";
import { worldToChunk, worldToLocal } from "./coordinates";

/** A single block edit, in world coordinates. */
export type BlockOverrideEntry = {
  wx: number;
  wy: number;
  wz: number;
  blockId: number;
};

export type BlockOverrides = {
  /** Record an edit (last write wins). */
  set: (wx: number, wy: number, wz: number, blockId: number) => void;
  /** Get the override at a world position, or undefined if none. */
  get: (wx: number, wy: number, wz: number) => number | undefined;
  /** Apply all overrides belonging to a chunk onto its block buffer. */
  applyToChunk: (blocks: Uint16Array, cx: number, cy: number, cz: number) => void;
  /** Flatten to a serializable list (for network transfer). */
  toList: () => BlockOverrideEntry[];
  size: () => number;
  clear: () => void;
};

/**
 * Sparse map of block edits on top of deterministic terrain.
 * Keyed by chunk so re-generated chunks can be patched cheaply.
 */
export function createBlockOverrides(initial?: BlockOverrideEntry[]): BlockOverrides {
  // chunk key "cx,cy,cz" → (block index within chunk → blockId)
  const byChunk = new Map<string, Map<number, number>>();
  let count = 0;

  function set(wx: number, wy: number, wz: number, blockId: number): void {
    const { cx, cy, cz } = worldToChunk(wx, wy, wz);
    const { x: lx, y: ly, z: lz } = worldToLocal(wx, wy, wz);
    const key = `${cx},${cy},${cz}`;
    let chunkMap = byChunk.get(key);
    if (!chunkMap) {
      chunkMap = new Map();
      byChunk.set(key, chunkMap);
    }
    const idx = getBlockIndex(lx, ly, lz);
    if (!chunkMap.has(idx)) count++;
    chunkMap.set(idx, blockId);
  }

  function get(wx: number, wy: number, wz: number): number | undefined {
    const { cx, cy, cz } = worldToChunk(wx, wy, wz);
    const { x: lx, y: ly, z: lz } = worldToLocal(wx, wy, wz);
    return byChunk.get(`${cx},${cy},${cz}`)?.get(getBlockIndex(lx, ly, lz));
  }

  function applyToChunk(blocks: Uint16Array, cx: number, cy: number, cz: number): void {
    const chunkMap = byChunk.get(`${cx},${cy},${cz}`);
    if (!chunkMap) return;
    for (const [idx, blockId] of chunkMap) {
      blocks[idx] = blockId;
    }
  }

  function toList(): BlockOverrideEntry[] {
    const list: BlockOverrideEntry[] = [];
    for (const [key, chunkMap] of byChunk) {
      const [cx, cy, cz] = key.split(",").map(Number);
      for (const [idx, blockId] of chunkMap) {
        const lx = idx % CHUNK_SIZE;
        const lz = Math.floor(idx / CHUNK_SIZE) % CHUNK_SIZE;
        const ly = Math.floor(idx / (CHUNK_SIZE * CHUNK_SIZE));
        list.push({
          wx: cx * CHUNK_SIZE + lx,
          wy: cy * CHUNK_SIZE + ly,
          wz: cz * CHUNK_SIZE + lz,
          blockId,
        });
      }
    }
    return list;
  }

  function size(): number {
    return count;
  }

  function clear(): void {
    byChunk.clear();
    count = 0;
  }

  if (initial) {
    for (const entry of initial) {
      set(entry.wx, entry.wy, entry.wz, entry.blockId);
    }
  }

  return { set, get, applyToChunk, toList, size, clear };
}
